import {
  type Product,
  ProductAutocompleteDocument,
  type ProductAutocompleteQuery,
  type ProductAutocompleteQueryVariables,
} from "@/generated/graphql";
import type {
  AutocompleteFetchParams,
  AutocompleteFetchResult,
} from "@k3-tech/react-kit";
import { useCallback } from "react";
import { useClient } from "urql";
import { formatProductLabel } from "@/features/product/utils/formatProductLabel";

type UseProductAutocompleteConfig = {
  searchKey?: "q" | "searchKeyFull";
};

export const useProductAutocomplete = (config?: UseProductAutocompleteConfig) => {
  const urqlClient = useClient();
  const searchKey = config?.searchKey ?? "q";

  const load = useCallback(
    async (
      page: number,
      pageSize: number,
      search?: string,
      moreFilter?: AutocompleteFetchParams["moreFilter"]
    ) => {
      const { data } = await urqlClient
        .query<ProductAutocompleteQuery, ProductAutocompleteQueryVariables>(
          ProductAutocompleteDocument,
          {
            qop: {
              pagination: {
                page,
                pageSize,
                withCount: true,
              },
              filters: {
                isActive: true, // only fetch active product by default
                ...(search
                  ? searchKey === "searchKeyFull"
                    ? { searchKeyFull: search }
                    : { q: search }
                  : {}),
                ...(moreFilter?.() || {}),
              },
            },
          },
          { suspense: false, requestPolicy: "network-only" }
        )
        .toPromise();

      const rawItems = (data?.products?.items ?? []) as NonNullable<
        ProductAutocompleteQuery["products"]
      >["items"];

      const items = (rawItems || [])
        .filter((it): it is NonNullable<typeof it> => Boolean(it))
        .map((item) => ({
          value: item.productID,
          label: formatProductLabel({ barcode: item.barcode, productName: item.productName }),
          raw: item,
        }));

      return {
        items: items ?? [],
        total: data?.products?.pagination?.totalRows ?? 0,
        hasMore: data?.products?.pagination?.hasNext ?? false,
      } as AutocompleteFetchResult<Product>;
    },
    [urqlClient, searchKey]
  );

  return { load };
};
